import { useCallback, useEffect, useRef, useState } from "react";
import { useApi } from "./useApi";

const LIMIT = 5;

// The identities and devices with the most open findings the user can read, for the Security
// Operations Overview: GET /defender-identity/identities/top-findings and .../devices/top-findings.
// Each list is undefined while loading, null when it can't be read (feature off, failure), else the
// rows. A list is read only when its inventory is connected. `pollKey` reloads silently (the current
// lists stay; an auth error never redirects; a failure keeps them); a tick is skipped while any read
// is still running, so it never supersedes one and leaves a list loading.
export const useSocTopExposed = ({ identities: wantIdentities, devices: wantDevices, reloadKey = 0, pollKey = 0 }) => {
  const apiCall = useApi();
  const [identities, setIdentities] = useState(undefined);
  const [devices, setDevices] = useState(undefined);
  const seqRef = useRef(0);
  const inFlightRef = useRef(0);

  const load = useCallback(async (silent) => {
    if (silent && inFlightRef.current > 0) return;
    seqRef.current += 1;
    const seq = seqRef.current;
    inFlightRef.current += 1;
    const read = async (want, path, set) => {
      if (!want) { set(null); return; }
      try {
        const rows = await apiCall("GET", `/defender-identity/${path}/top-findings?limit=${LIMIT}`, null, false, null, silent);
        if (seq === seqRef.current) set(Array.isArray(rows) ? rows : null);
      } catch {
        if (seq === seqRef.current && !silent) set(null);
      }
    };
    try {
      await Promise.all([read(wantIdentities, "identities", setIdentities), read(wantDevices, "devices", setDevices)]);
    } finally {
      inFlightRef.current -= 1;
    }
  }, [apiCall, wantIdentities, wantDevices]);

  useEffect(() => {
    load(false);
  }, [load, reloadKey]);

  useEffect(() => {
    if (pollKey > 0) load(true);
    // Only a new poll tick triggers a silent load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollKey]);

  return { identities, devices };
};

export default useSocTopExposed;
